import classNames from "classnames";
import isEmpty from "is-empty";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import styles from "./Select.module.scss";
import { ColorVariantType, SelectOptionType } from "../../../../types";
import { useConfig, useSession } from "@hooks";
import { getAbsoluteImagePath } from "@utils/dom";

export interface SelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement>,
    ColorVariantType {
  shape?: "default" | "underline";
  options?: SelectOptionType[] | (() => JSX.Element);
  additionalClassname?: string;
  value?: string | number | readonly string[] | undefined;
  loadingPlaceholder?: string;
  error?: boolean;
  empty?: boolean;
  defaultSetter?: (value?: any) => void;
}

const languagesVarsCodes = [
  "LANG_neobooking_please_select", //Veuillez sélectionner
];

const Select: React.FC<SelectProps> = ({
  shape = "underline",
  value,
  options,
  variant = "primary",
  additionalClassname,
  error,
  empty,
  placeholder,
  loadingPlaceholder,
  defaultSetter,
  ...props
}) => {
  /* Hooks */
  const { getLanguageVarValue } = useSession();
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";

  /* State */
  const [placeholderOption, setPlaceholderOption] =
    useState<string | null>(null);

  useEffect(() => {
    empty &&
      setPlaceholderOption(
        placeholder || getLanguageVarValue("LANG_neobooking_please_select")
      );
    loadingPlaceholder && isEmpty(options)
      ? setPlaceholderOption(loadingPlaceholder)
      : setPlaceholderOption(null);
  }, [options, empty]);

  useEffect(() => {
    defaultSetter && defaultSetter();
  }, []);

  const SELECT_CLASS = {
    base: `disabled:tw-opacity-40 tw-leading-8 tw-outline-none tw-transition-colors tw-duration-200 tw-ease-in-out tw-border-v_blue-dark tw-text-v_blue-dark tw-py-1 tw-px-3 tw-rounded-full`,
    shape: {
      default: `tw-border`,
      underline: `tw-border-b-1 tw-border-b tw-rounded-none`,
    },
    variant: {
      default: `focus:tw-border-gray-400`,
      primary: `${
        mode === "vacanceole"
          ? "focus:tw-border-v_blue-dark"
          : "focus:tw-border-primary_corseole-500"
      }`,
      secondary: `focus:tw-border-secondary-500`,
      info: `focus:tw-border-primary-500`,
      success: `focus:tw-border-green-500`,
      warning: `focus:tw-border-yellow-500`,
      danger: `focus:tw-border-red-500`,
    },
    error: `tw-border-red-500`,
  };

  const defaultClass = classNames(
    SELECT_CLASS.base,
    error ? SELECT_CLASS.error : SELECT_CLASS.variant[variant],
    SELECT_CLASS.shape[shape],
    additionalClassname
  );
  let customClass = defaultClass;
  if (props.className) {
    customClass = props.className;
  }
  customClass = classNames(customClass, styles.select);

  return (
    <div className="select_box">
      <select
        value={value}
        defaultValue={placeholderOption || (placeholder && "")}
        className={customClass}
        {...props}
      >
        {placeholderOption ? (
          <option value="">{placeholderOption}</option>
        ) : (
          placeholder && <option value="">{placeholder}</option>
        )}
        {Array.isArray(options)
          ? options?.map((item, index) => {
              return (
                <option
                  selected={options.length === 1}
                  key={index}
                  value={item.value}
                >
                  {item.label}
                </option>
              );
            })
          : options
          ? options()
          : undefined}
      </select>
    </div>
  );
};

Select.propTypes = {
  options: PropTypes.any.isRequired,
  additionalClassname: PropTypes.string,
  error: PropTypes.bool,
};

export default Select;
